import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { DAILY, DAY_MS, dayIndex } from '@il-colpo/meta/progress';
import type { Issue, JoinError } from '@il-colpo/protocol';
import { formatTag } from '@il-colpo/shared/account';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { useLooseT } from '../../game/text/loose.ts';
import { useFlag } from '../../meta/liveStore.ts';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import type { DailyRow, DailyRun, DailyStatus } from '../../meta/types.ts';
import { getConnection } from '../../online/onlineConnection.ts';
import { useNow } from '../../systems/useNow.ts';
import { Badge, Button, Panel, ScreenFrame } from '../../ui/index.ts';
import { IssueLine } from '../online/shared.tsx';
import { Failed, GatePanel, Page, useMetaGate } from './common.tsx';
import styles from './Meta.module.css';

function RunLine({ run }: { run: DailyRun }) {
  const { t } = useTranslation('meta');
  return (
    <li className={styles['recent']}>
      <strong>{t('daily.attemptLine', { n: run.attempt })}</strong>
      <span className={styles['row']}>
        {run.loot === null ? (
          <Badge tone="neutral">{t('daily.left')}</Badge>
        ) : run.success ? (
          <Badge tone="green">{t('daily.loot', { amount: run.loot })}</Badge>
        ) : (
          <Badge tone="neutral">{t('daily.failed')}</Badge>
        )}
        {run.durationMs !== null ? (
          <span className={styles['note']}>
            {t('daily.timeUnit', { seconds: Math.round(run.durationMs / 1000) })}
          </span>
        ) : null}
      </span>
    </li>
  );
}

function BoardRowItem({ row }: { row: DailyRow }) {
  const { t } = useTranslation('meta');
  return (
    <li className={styles['boardRow']} data-me={row.isMe}>
      <span className={styles['position']}>{row.position}</span>
      <AvatarFigure avatar={row.avatar} size="2.6rem" />
      <span className={styles['who']}>
        <strong>{`${row.nickname}#${formatTag(row.tag)}`}</strong>
        <span className={styles['note']}>
          {t('daily.timeUnit', { seconds: Math.round(row.durationMs / 1000) })}
          {row.isMe ? ` · ${t('daily.you')}` : ''}
        </span>
      </span>
      <span className={styles['score']}>{t('daily.loot', { amount: row.loot })}</span>
    </li>
  );
}

function Today({ status }: { status: DailyStatus }) {
  const { t } = useTranslation('meta');
  const g = useLooseT('game');
  const navigate = useNavigate();
  const now = useNow();
  const [pending, setPending] = useState(false);
  const [issue, setIssue] = useState<Issue | null>(null);
  const left = Math.max(0, DAILY.attempts - status.runs.length);
  const msToNext = (dayIndex(now) + 1) * DAY_MS - now;
  const hours = Math.floor(msToNext / 3_600_000);
  const minutes = Math.floor((msToNext % 3_600_000) / 60_000);
  const day = status.today;

  const play = () => {
    setPending(true);
    setIssue(null);
    getConnection()
      .dailyRun()
      .then(() => {
        void navigate('/online/room');
      })
      .catch((error: unknown) => {
        setIssue((error as Partial<JoinError>).issue ?? 'unreachable');
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <>
      <Panel title={t('daily.today')}>
        {day ? (
          <div className={styles['stack']}>
            <dl className={styles['stats']}>
              <div className={styles['stat']}>
                <dt>{t('daily.map')}</dt>
                <dd>{g(`maps.${day.mapId}.name`)}</dd>
              </div>
              <div className={styles['stat']}>
                <dt>{t('daily.role')}</dt>
                <dd>{g(`roles.${day.role}`)}</dd>
              </div>
              <div className={styles['stat']}>
                <dt>{t('daily.difficulty')}</dt>
                <dd>{g(`difficulty.${day.difficulty}`)}</dd>
              </div>
            </dl>
            <p className={styles['note']}>
              {left > 0 ? t('daily.attemptsLeft', { count: left }) : t('daily.attemptsNone')}
            </p>
            <div>
              <Button icon="play" disabled={left === 0} loading={pending} onClick={play}>
                {t('daily.play')}
              </Button>
            </div>
            <IssueLine issue={issue} />
            <p className={styles['note']}>{t('daily.next', { hours, minutes })}</p>
            {status.tomorrow ? (
              <p className={styles['note']}>
                {t('daily.tomorrow', {
                  map: g(`maps.${status.tomorrow.mapId}.name`),
                  role: g(`roles.${status.tomorrow.role}`),
                })}
              </p>
            ) : null}
          </div>
        ) : (
          <p className={styles['note']}>{t('daily.notReady')}</p>
        )}
      </Panel>

      {status.runs.length > 0 ? (
        <Panel title={t('daily.attempts')}>
          <ul className={styles['list']}>
            {status.runs.map((r) => (
              <RunLine key={r.attempt} run={r} />
            ))}
          </ul>
        </Panel>
      ) : null}

      <Panel title={t('daily.board')}>
        <div className={styles['stack']}>
          {status.board.length === 0 ? (
            <p className={styles['note']}>{t('daily.boardEmpty')}</p>
          ) : (
            <ol className={styles['board']}>
              {status.board.map((r) => (
                <BoardRowItem
                  key={`${String(r.position)}-${r.nickname}-${String(r.tag)}`}
                  row={r}
                />
              ))}
            </ol>
          )}
          <p className={styles['note']}>{t('daily.boardHint')}</p>
        </div>
      </Panel>
    </>
  );
}

/** `/daily`: today's heist, your three attempts, and the board. */
export function DailyScreen() {
  const { t } = useTranslation('meta');
  const gate = useMetaGate();
  const enabled = useFlag('daily');
  const meta = useMetaClient();
  const load = useMemo(
    () => (meta && gate === 'ready' && enabled ? () => meta.daily(Date.now()) : null),
    [meta, gate, enabled],
  );
  const status = useRemote<DailyStatus>(load);
  return (
    <ScreenFrame title={t('daily.title')} backTo="/">
      <Page>
        <p className={styles['note']}>{t('daily.intro')}</p>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        {gate === 'ready' && !enabled ? (
          <Panel>
            <p className={styles['note']}>{t('news.off')}</p>
          </Panel>
        ) : null}
        {status.status === 'error' ? <Failed onRetry={status.reload} /> : null}
        {gate === 'ready' && enabled && status.status === 'loading' ? (
          <GatePanel gate="loading" />
        ) : null}
        {status.data ? <Today status={status.data} /> : null}
      </Page>
    </ScreenFrame>
  );
}
